/**
 * Comparison toolbar toggle, preference sync, and cell caption (WSRADM-0052).
 */
import { useEffect, useRef } from 'react';
import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import type { ComparisonBlock } from '@/api/reports/types';
import { readScalarViewPref, writeScalarViewPref } from '@/config/viewPrefs';
import { useCopy } from '@/hooks/useCopy';

export type CompareMode = 'off' | 'lastYear' | 'trailing3';

export function useComparePref(prefsKey: 'membershipCompare' | 'revenueCompare') {
  const [searchParams, setSearchParams] = useSearchParams();
  const seededRef = useRef(false);
  const param = searchParams.get('compare');
  const mode: CompareMode =
    param === 'lastYear' || param === 'trailing3' ? param : 'off';

  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (!searchParams.has('compare')) {
      const remembered = readScalarViewPref(prefsKey);
      if (remembered === 'lastYear' || remembered === 'trailing3') {
        const next = new URLSearchParams(searchParams);
        next.set('compare', remembered);
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, prefsKey]);

  const setMode = (nextMode: CompareMode) => {
    const next = new URLSearchParams(searchParams);
    if (nextMode === 'off') {
      next.delete('compare');
    } else {
      next.set('compare', nextMode);
    }
    setSearchParams(next);
    writeScalarViewPref(prefsKey, nextMode === 'off' ? null : nextMode);
  };

  return [mode, setMode] as const;
}

export interface CompareToggleProps {
  value: CompareMode;
  onChange: (mode: CompareMode) => void;
}

export function CompareToggle({ value, onChange }: CompareToggleProps) {
  const { t } = useCopy();
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      aria-label={t('reports.compare.label', 'Compare')}
      value={value}
      onChange={(_event, next: CompareMode | null) => {
        if (next === null) return;
        onChange(next);
      }}
    >
      <ToggleButton value="off">{t('reports.compare.off', 'No comparison')}</ToggleButton>
      <ToggleButton value="lastYear">
        {t('reports.compare.lastYear', 'Same month last year')}
      </ToggleButton>
      <ToggleButton value="trailing3">
        {t('reports.compare.trailing3', '3-month average')}
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

export interface CompareCaptionProps<T> {
  comparisons: {
    sameMonthLastYear: ComparisonBlock<T>;
    trailing3MonthAverage: ComparisonBlock<T>;
  } | null | undefined;
  mode: CompareMode;
  getMetric: (values: T) => number | null;
  formatValue: (val: number, mode: 'lastYear' | 'trailing3') => string;
  testId?: string;
}

export function CompareCaption<T>({
  comparisons,
  mode,
  getMetric,
  formatValue,
  testId,
}: CompareCaptionProps<T>) {
  const { t } = useCopy();
  if (mode === 'off' || !comparisons) return null;
  const block = mode === 'lastYear' ? comparisons.sameMonthLastYear : comparisons.trailing3MonthAverage;
  if (!block) return null;

  const prefix = mode === 'lastYear'
    ? t('reports.compare.prefixLastYear', 'LY')
    : t('reports.compare.prefixTrailing3', '3M');

  let text = '—';
  if (block.available && block.values !== null) {
    const val = getMetric(block.values);
    if (val !== null) {
      text = `${prefix} ${formatValue(val, mode)}`;
    }
  }

  const monthsText = block.months ? block.months.join(', ') : (block.month ?? '');
  const tooltipTitle = monthsText
    ? t('reports.compare.monthsTooltip', '{{months}}', { months: monthsText })
    : '';

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', fontSize: 11, lineHeight: 1.2, mt: 0.25 }}
      title={tooltipTitle || undefined}
      data-testid={testId ? `compare-caption-${testId}` : 'compare-caption'}
    >
      {text}
    </Typography>
  );
}
